import { Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild, ViewContainerRef } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MatSidenav, MatSidenavContent } from '@angular/material/sidenav';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription, combineLatest, debounceTime, map, take } from 'rxjs';
import { AuthorizeControlService } from 'pr1-ui-components';
import { ConfirmDialogComponent, DEFAULT_SNACKBAR_CONFIG, Logger, NotificationService, OrganizationService, } from 'src/app/@shared';
import { Filter } from 'src/app/@shared/models/filter.model';
import { EditDatesDialogComponent, EditOfferTagsComponent, Event, EventService, Offer, OfferDomain, OfferPromoSortDialogComponent, OfferTag, OfferTagsCount, OfferTagService, PageMapping, ProductCategory, ProductCategoryService, SettingsService, Version, VersionListComponent, VersionService, } from '../..';
import { OfferPromo } from 'src/app/modules/standard/v1';
import { PromoDomain } from 'src/app/modules/standard/v1';
import { OfferPromoService } from 'src/app/modules/standard/v1';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';
import { Status } from '../../models/offer-promo-status';
import { StatusCount } from '../../models/status-count.model';
import * as dayjs from 'dayjs';
import { EventWorkflowService } from '../../services/event-workflow.service';
import { CloneComponent } from '../clone/clone.component';
import { IPrintPage } from '../../models/print-page.model';
import { EditPageComponent } from '../../components/edit-page/edit-page.component';
import { ListViewStateService } from '../../services/list-view-state.service';
import { PageEvent } from '@angular/material/paginator';
import { SelectedRecordsDialogComponent } from '../../components/selected-records-dialog/selected-records-dialog.component';
import { OfferService } from '../../services/offer.service';

const log = new Logger('OffersComponent');
@Component({
  selector: 'app-offers',
  templateUrl: './offers.component.html',
  styleUrls: ['./offers.component.scss'],
})
export class OffersComponent<TEvent extends Event, Tag extends OfferTag, TOfferPromo extends OfferPromo, TPromoDomain extends PromoDomain<OfferPromo>, TProductCategory extends ProductCategory> implements OnInit, OnDestroy {
  public offerSorting = [
    { id: { active: 'PromoName', direction: 'asc' }, value: 'Offer Name asc' },
    {
      id: { active: 'PromoName', direction: 'desc' },
      value: 'Offer Name desc',
    },
    { id: { active: 'StartDate', direction: 'asc' }, value: 'Start date asc' },
    {
      id: { active: 'StartDate', direction: 'desc' },
      value: 'Start date desc',
    },
    { id: { active: 'Rank', direction: 'asc' }, value: 'Rank asc' },
    { id: { active: 'Rank', direction: 'desc' }, value: 'Rank desc' },
    { id: { active: 'PageNumber', direction: 'asc' }, value: 'PageNumber asc' },
  ];
  public offerpromos = [
    { name: 'Offers', value: 'OFFER' },
    { name: 'Promos', value: 'PROMO' },
    { name: 'Offer Group', value: 'OFFERGROUP' },
  ];

  public offerstatus = [
    { name: 'Approved', value: 'Approved' },
    { name: 'Pending', value: 'Pending' },
    { name: 'Suppressed', value: 'Suppressed' },
  ];

  public commentStatus = [
    'PENDING',
    'RESOLVED',
    'REJECTED'
  ]

  public statusCount: StatusCount = {};
  @ViewChild(MatSidenav) sidenav!: MatSidenav;
  @ViewChild('filterSidenav', { read: ElementRef }) filterSidenavElement?: ElementRef<HTMLElement>;
  @ViewChild('filterToggleButton', { read: ElementRef }) filterToggleButton?: ElementRef<HTMLElement>;
  @ViewChild('offerPromoTable', { static: false }) offerPromoTable: any;
  @ViewChild('offerPromoCards', { static: false }) offerPromoCards: any;
  offerTagsListwithCount: OfferTagsCount[] = [];
  printPageList: PageMapping = {};
  categoriesList: TProductCategory[] = [];
  eventDetails: any;
  viewModel$ = combineLatest([
    this.eventService.getEvent(this.route.snapshot.params['eventId']),
    this.offerpromoService.isLoading$,
    this.offerpromoService.viewMode$,
    this.offerpromoService.eventId$,
    this.offerpromoService.filters$,
    this.versionService.allVersions$,
    this.offerpromoService.workflowStages$,
    this.offerpromoService.getAllEventOfferTypesByEventId$,
    this.offerpromoService.totalRecords$,
    this.offerpromoService.page$,
    this.offerpromoService.isLoading$
  ]).pipe(
    map(
      ([event, isLoading, viewMode, eventId, filters, versions, workflowStages, eventOfferTypes, totalRecords, page, loading]) => {
        this.eventDetails = event;

        //version filters
        const versionFilters = filters.filter(f => f.fieldName === 'versionId')
          .map(f => versions.find(d => d.Id === f.value)).filter(d => d !== undefined) as Version[];

        //status filters
        const statusFilters = filters.filter(f => f.fieldName === 'status')
          .map(f => ({ name: f.displayText, value: f.value }));

        //offerTag filters
        const offerTagFilters = filters.filter(f => f.fieldName === 'offerTagId')
          .map(f => ({ TagId: f.value }));

        //eventOfferType filters
        const eventTypeFilters = filters.filter(f => f.fieldName === 'eventtype')
          .map(f => ({ name: f.displayText, value: f.value }));

        //page filters
        const pageIdsFilters = filters.filter(f => f.fieldName === 'pageids')
          .map(f => f.value);

        //category filters
        const categoryFilters = filters.filter(f => f.fieldName === 'categoryId')
          .map(f => ({ Id: f.value }));

        //commentStatuses filters
        const commentStatusesFilter = filters.filter(f => f.fieldName === 'commentStatuses')

        //workflowStages filters
        const workflowStageFilters = filters.filter(f => f.fieldName === 'workflowStages')

        //noTags filters
        const noTagsFilter = filters.find(f => f.fieldName === 'noTags');

        //noVersions filters
        const noVersionsFilter = filters.find(f => f.fieldName === 'noVersions');

        //hasComments filters
        const hasCommentsFilter = filters.find(f => f.fieldName === 'hasComments');

        //hasVariants filters
        const hasVariantsFilter = filters.find(f => f.fieldName === 'hasVariants');

        //hasTranslation filters
        const hasTranslationFilter = filters.find(f => f.fieldName === 'hasTranslation');

        this.filtersForm.patchValue({
          versions: versionFilters,
          offerStatus: statusFilters,
          offerTags: offerTagFilters,
          offerPromos: eventTypeFilters,
          page: pageIdsFilters,
          categories: categoryFilters,
          commentStatuses: commentStatusesFilter.length > 0 ? commentStatusesFilter.map(f => f.value) : [],
          workflowStages: workflowStageFilters.length > 0 ? workflowStageFilters.map(f => f.value) : [],
          noTags: noTagsFilter ? !!noTagsFilter.value : false,
          withComments: hasCommentsFilter ? !!hasCommentsFilter.value : false,
          withVariants: hasVariantsFilter ? !!hasVariantsFilter.value : false,
          noVersions: noVersionsFilter ? !!noVersionsFilter.value : false,
          withTranslation: hasTranslationFilter ? !!hasTranslationFilter.value : false
        });
        return { event, isLoading, viewMode, filters, versions, eventId, workflowStages, eventOfferTypes, totalRecords, page, loading };
      }
    )
  );

  filtersForm = new FormGroup({
    search: new FormControl(),
    versions: new FormControl(),
    offerTags: new FormControl(),
    offerPromos: new FormControl(),
    offerStatus: new FormControl(),
    page: new FormControl(),
    categories: new FormControl(),
    noTags: new FormControl<boolean>(false),
    withComments: new FormControl<boolean>(false),
    commentStatuses: new FormControl<string[]>([]),
    workflowStages: new FormControl<string[]>([]),
    withVariants: new FormControl<boolean>(false),
    noVersions: new FormControl<boolean>(false),
    withTranslation: new FormControl<boolean>(false)
  });
  selectedTag = '';
  applicableWorkflowPermissions: any = null;
  awDetails: any = null;
  showStageApproval: boolean = false;
  activeStage: any = null;
  activeStageIndex: number = -1;
  stageCompleted = false;
  stageToMarkActive: any = null;
  readOnlyMode = true;
  permissionExists: boolean = false;
  roles = {
    reviewer: 0,
    approver: 1,
    editor: 2,
  };

  subscription$ = new Subscription();
  userHasAccess: boolean = false;
  selectedVal = '';
  loggedInUsersRole: any;
  gotPermissions: boolean = false;
  permissions: any;
  inProgressWorkflowIndex: any;
  printPagesList: IPrintPage[] = [];
  showComments: boolean = false;
  showChangesFromPreviousStep = false;
  modifiedItems: string[] = [];
  private scrollSub?: Subscription;
  private _scrollContent!: MatSidenavContent;
  private scrollRestored = false;
  previousStage: any = null;

  @ViewChild('scrollContent')
  set scrollContentSetter(content: MatSidenavContent) {
    if (!content) return;
    this._scrollContent = content;
    this.scrollSub?.unsubscribe();

    this.scrollSub = content
      .elementScrolled()
      .pipe(debounceTime(300))
      .subscribe(() => {
        const scrollTop = content.measureScrollOffset('top');
        this.listViewState.updateListViewState({ scrollPosition: scrollTop });
      });
  }

  constructor(
    private eventService: EventService<TEvent>,
    private offerpromoService: OfferPromoService<TOfferPromo, TPromoDomain>,
    private versionService: VersionService<Version>,
    private offerTagService: OfferTagService<Tag>,
    private categoryService: ProductCategoryService<TProductCategory>,
    private route: ActivatedRoute,
    private dialog: MatDialog,
    private matSnackBar: MatSnackBar,
    private ewService: EventWorkflowService,
    private orgService: OrganizationService,
    private notificationService: NotificationService,
    private authorizationService: AuthorizeControlService,
    private router: Router,
    private settingService: SettingsService,
    public listViewState: ListViewStateService,
    private viewContainerRef: ViewContainerRef,
    private offerService: OfferService<Offer, OfferDomain<Offer>>,
  ) { }

  get variantViewMode(): 'compressed' | 'expanded' {
    return this.listViewState.listViewStateValue.variantViewMode;
  }

  ngOnInit(): void {
    const eventId = this.route.snapshot.params['eventId'] || this.offerpromoService.eventId;
    this.offerpromoService.GetAllPrintPagesByEventforOffers(eventId).subscribe((res) => {
      this.printPagesList = res;
    });
    this.subscription$.add(
      combineLatest([
        this.offerTagService.getByOfferTagsWithCount(eventId, []),
        this.offerTagService.GetPrintPageMetadataByEvent(eventId),
        this.categoryService.getCategoriesByEventId(eventId),
      ]).pipe(take(1))
        .subscribe(([tagsData, printPageData, categoriesData]) => {
          this.offerTagsListwithCount = tagsData.filter(tag => tag.TagId && tag.TagName);
          this.printPageList = printPageData;
          this.categoriesList = categoriesData;
        })
    );

    this.getWorkflowPermissions();
    this.processRequiredInitialSetup();
    this.manuallyInvokePermissionsForCurrentWorkflow();
    this.getIntegrations();
    this.offerpromoService.sort$.subscribe(sort => {
      this.listViewState.updateListViewState({ sort: { ...sort, direction: sort.direction as "asc" | "desc" } });
    });

    // this.offerpromoService.sort(this.offerSorting[0].id);
  }

  ngOnDestroy(): void {
    this.listViewState.clearSelection();
    this.subscription$.unsubscribe();
    this.scrollSub?.unsubscribe();
  }

  onContentRendered() {
    if (this.scrollRestored) return;

    const scroll = this.listViewState.listViewStateValue.scrollPosition;
    this.restoreScroll(scroll);
    this.scrollRestored = true;
  }

  restoreScroll(position: number) {
    if (!this._scrollContent || !position) return;

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this._scrollContent.scrollTo({ top: position });
      });
    });
  }

  onPage(pageEvent: PageEvent): void {
    this.offerpromoService.page(pageEvent);
    this.listViewState.updateListViewState({ pageIndex: pageEvent.pageIndex, pageSize: pageEvent.pageSize, scrollPosition: 0 });
  }

  toggleVariantView(): void {
    const next: 'compressed' | 'expanded' = this.variantViewMode === 'expanded' ? 'compressed' : 'expanded';
    this.listViewState.updateListViewState({ variantViewMode: next });
  }

  onShowChangesFromPreviousStepChange(checked: boolean): void {
    this.showChangesFromPreviousStep = checked;
    this.offerService.setShowChangesFromPreviousStep(checked);

    if (!checked) {
      this.modifiedItems = [];
      return;
    }

    const eventId = this.route.snapshot.params['eventId'];
    const workflowTaskId = this.previousStage?.StageId;

    if (!eventId || !workflowTaskId) {
      this.showChangesFromPreviousStep = false;
      this.offerService.setShowChangesFromPreviousStep(false);
      this.matSnackBar.open(
        'Unable to load changes. Workflow stage is not available.',
        'OK',
        DEFAULT_SNACKBAR_CONFIG
      );
      return;
    }

    this.subscription$.add(
      this.offerService.getModifiedEntitiesByWorkflowTask(eventId, workflowTaskId).subscribe({
        next: (modifiedItems: string[]) => {
          this.modifiedItems = (modifiedItems ?? []).filter((id) => !!id);
        },
        error: () => {
          this.showChangesFromPreviousStep = false;
          this.modifiedItems = [];
          this.offerService.setShowChangesFromPreviousStep(false);
          this.matSnackBar.open(
            'Failed to load changes from previous step',
            'OK',
            DEFAULT_SNACKBAR_CONFIG
          );
        },
      })
    );
  }

  getIntegrations() {
    this.settingService.getIntegrations().subscribe((res: any) => {
      this.settingService.addIntegrations(res);
      const conmmentsConfig = res.find((i: any) => i.ClientKey === 'retailercomments');
      if (conmmentsConfig) {
        this.showComments = conmmentsConfig.Status;
      }
    });
  }

  manuallyInvokePermissionsForCurrentWorkflow() {
    if (this.ewService.getSelectedEventId()?.trim().length == 0) {
      this.ewService.setSelectedEventId(
        this.route.snapshot.params['eventId']?.trim()
      );
    }
  }

  navigateOfferGroup() {
    this.router.navigate(['offergroup'], {
      relativeTo: this.route,
      queryParamsHandling: 'preserve'
    });
  }

  isSelected(viewModel: any, status: string) {
    return (
      viewModel.filters &&
      viewModel.filters.length > 0 &&
      viewModel.filters.find((x: any) => x.displayText === status)
    );
  }

  onValChange(val: string) {
    this.selectedVal = val;
    const selectedStatus = this.offerstatus.filter((x) => val.includes(x.name));
    this.filtersForm.controls.offerStatus.patchValue(selectedStatus);
    this.filterOfferStatus(selectedStatus);
  }

  processRequiredInitialSetup() {
    let initialState = this.listViewState.listViewStateValue;
    this.offerpromoService.sort(initialState.sort);
    this.offerpromoService.toggleViewMode(initialState.viewMode);
    setTimeout(() => {
      this.offerpromoService.page({ pageIndex: initialState.pageIndex, pageSize: initialState.pageSize });
    }, 500);
    if (initialState.filters && Object.keys(initialState.filters).length > 0) {
      this.filtersForm.patchValue(initialState.filters);
      this.offerpromoService.search(initialState.filters?.['search'] || '');
    } else {
      this.clearSearch();
      this.clearFilters();
    }

    this.listViewState.updateListViewState({ pageIndex: initialState.pageIndex, pageSize: initialState.pageSize });

    this.offerpromoService.eventId = this.route.snapshot.params['eventId'];
    this.versionService.eventId = this.route.snapshot.params['eventId'];
    this.offerpromoService.statusUpdated$.subscribe((res) => {
      this.getStatusCount();
    });
  }
  getWorkflowPermissions() {
    setTimeout(() => {
      if (!this.permissionExists) {
        this.readOnlyMode = !this.authorizationService.checkAccess('edit||offermang');
      }
    }, 1000);
    if (this.offerPromoTable) {
      this.offerPromoTable.updateReadonlyMode(this.readOnlyMode);
    } else if (this.offerPromoCards) {
      this.offerPromoCards.updateReadonlyMode(this.readOnlyMode);
    }

    this.subscription$.add(
      this.ewService.permissionForCurrentWorkflow$.subscribe(
        (permissions: any) => {
          this.permissions = permissions;
          // If no permissions found, restrict access
          if (!permissions || permissions.length == 0) {
            this.showStageApproval = false;
            return;
          }

          const inProgressWorkflowIndex = permissions.findIndex(
            (p: any) => p.Status == 'in_progress'
          );
          this.inProgressWorkflowIndex = inProgressWorkflowIndex;

          // If an active workflow exists
          if (inProgressWorkflowIndex > -1) {
            this.applicableWorkflowPermissions =
              permissions[inProgressWorkflowIndex];

            this.getWorkflowDetails(
              this.applicableWorkflowPermissions['WorkflowId']
            );
            const stages = this.applicableWorkflowPermissions?.Stages;
            console.log(`stages: `, stages);
            // TODO: Should check for only active stage, need to remove empty and not_started, after updating workflow editor to auto update active to next stage.!
            const activeStageIndex = stages.findIndex(
              (s: any) => s.Status == 'active' || s.Status?.trim() == '' || s.Status == 'not_started'
            );

            const previousStageIndex = stages.map((s: any) => s.Status).lastIndexOf('completed');
            if(previousStageIndex > -1) {
              this.previousStage = stages[previousStageIndex];
            }
            console.log(`previousStage: `, this.previousStage);
            this.activeStageIndex = activeStageIndex;
            if (this.offerService.isShowChangesFromPreviousStep && this.activeStageIndex >= 2) {
              this.onShowChangesFromPreviousStepChange(true);
            }
            // Check if active stage index
            if (activeStageIndex > -1) {
              this.activeStage =
                this.applicableWorkflowPermissions?.Stages[activeStageIndex];

              // Check if logged-in user has permission to access this event
              const userFoundIndex = this.activeStage?.Users?.findIndex(
                (u: any) =>
                  u.Email?.toLowerCase()?.trim() ==
                  this.orgService?.loggedInUser?.email?.toLowerCase().trim()
              );

              let user: any = null;
              let userCanOnlyView = true;

              if (userFoundIndex > -1) {
                // User has access to event in current workflow
                this.userHasAccess = true;

                user = this.activeStage?.Users[userFoundIndex];
                this.loggedInUsersRole = user.Role;
                if (user.Role == this.roles.reviewer) {
                  // User is reviewer & can only view the event
                  userCanOnlyView = true;
                } else {
                  // User is approver & can edit the event
                  userCanOnlyView = false;
                }
              } else {
                // User DOES NOT have access to event in current workflow
                // Allow only read only access
                userCanOnlyView = true;
                this.subscription$.unsubscribe();
              }

              // User is reviewer & hence has only read only access 
              this.readOnlyMode = userCanOnlyView;
              this.permissionExists = true;

              if (this.offerPromoTable) {
                this.offerPromoTable.updateReadonlyMode(this.readOnlyMode);
              } else if (this.offerPromoCards) {
                this.offerPromoCards.updateReadonlyMode(this.readOnlyMode);
              }

              // If user is permitted, continue with stage approval
              if (
                activeStageIndex <
                this.applicableWorkflowPermissions?.Stages?.length - 1
              ) {
                this.stageToMarkActive =
                  this.applicableWorkflowPermissions?.Stages[
                  activeStageIndex + 1
                  ];
              }

              this.showStageApproval = true;

              // Get permissions call doesnt have enough data, hence call get details
              this.getWorkflowDetails(
                this.applicableWorkflowPermissions['WorkflowId']
              );
            }
            // If there is NO Active STAGE - go into default mode
            else {
              this.showStageApproval = false;
            }
          }
          // If no workflow is active, just show events in default mode
          else {
            this.showStageApproval = false; // Stage sign off does not exist in default mode           
          }
          this.gotPermissions = true;
        }
      )
    );
  }

  getWorkflowDetails(workflowId: string) {
    this.ewService
      .getWorkflowDetailsForFeature(
        workflowId,
        this.orgService.silentAuthToken
      )
      .subscribe((details: any) => {
        this.awDetails = details;
      })
  }

  getKeys(obj: PageMapping): string[] {
    return Object.keys(obj);
  }

  openDialog(): void {
    const dialogRef = this.dialog.open(CloneComponent, {
      width: '80%',
      height: '90%',
      data: { eventId: this.route.snapshot.params['eventId'] },
    });
  }

  getStatusCount() {
    this.offerpromoService.getStatusCount().subscribe((count) => {
      this.statusCount = count;
    });
  }

  onSearch(event: any) {
    this.clearSelection();
    this.offerpromoService.search(event.target.value);
    this.listViewState.updateListViewState({ filters: this.filtersForm.value, pageIndex: 0, scrollPosition: 0, selectedRecords: [] });
  }

  onSort() {
    const dialogRef = this.dialog.open(OfferPromoSortDialogComponent);

    dialogRef.afterClosed().subscribe((data) => {
      if (data?.clearselection) {
        this.clearSelection();
      }
    });
  }
  clearSearch() {
    this.filtersForm.controls.search.setValue('');
    this.offerpromoService.search('');
    this.clearSelection();
  }

  toggleView(mode: string) {
    this.offerpromoService.toggleViewMode(mode);
    this.listViewState.updateListViewState({ viewMode: mode as 'CARDS' | 'TABLE' });
  }

  clearFilters() {
    this.filtersForm.reset();
    this.offerpromoService.search('');
    this.offerpromoService.clearFilters();
    this.listViewState.updateListViewState({ filters: this.filtersForm.value, pageIndex: 0, scrollPosition: 0, selectedRecords: [] });
    this.selectedVal = '';
    this.clearSelection();
  }

  clearSelection() {
    if (this.offerPromoTable) {
      this.offerPromoTable.clearSelection();
    } else if (this.offerPromoCards) {
      this.offerPromoCards.clearSelection();
    }
  }

  removeFilter(filter: Filter) {
    if (filter.fieldName === 'versionId') {
      ///TODO: fix below
      this.filtersForm.patchValue({
        versions: this.filtersForm.controls.versions.value.filter(
          (item: Version) => item.Id !== filter.value
        ),
      });
    }
    if (filter.fieldName === 'status') {
      ///TODO: fix below
      this.filtersForm.patchValue({
        offerStatus: this.filtersForm.controls.offerStatus.value.filter(
          (item: any) =>
            item.name.toLowerCase() !== filter.value.toLocaleLowerCase()
        ),
      });
      this.selectedVal = this.filtersForm.controls.offerStatus.value.map(
        (x: any) => x.name
      );
    }

    if (filter.fieldName === 'offerTagId') {
      this.filtersForm.patchValue({
        offerTags: this.filtersForm.controls.offerTags.value.filter(
          (item: OfferTagsCount) => item.TagId !== filter.value
        ),
      });
    }

    if (filter.fieldName === 'noTags') {
      this.filtersForm.controls.noTags.setValue(false);
    }

    if (filter.fieldName === 'hasComments') {
      this.filtersForm.controls.withComments.setValue(false);
    }

    if (filter.fieldName === 'hasVariants') {
      this.filtersForm.controls.withVariants.setValue(false);
    }

    if (filter.fieldName === 'noVersions') {
      this.filtersForm.controls.noVersions.setValue(false);
    }

    if (filter.fieldName === 'hasTranslation') {
      this.filtersForm.controls.withTranslation.setValue(false);
    }

    this.offerpromoService.removeFilter(filter);
    this.listViewState.updateListViewState({ filters: this.filtersForm.value, pageIndex: 0, scrollPosition: 0, selectedRecords: [] });
  }

  onFilterChange(event: any) {
    this.clearSelection();
    this.listViewState.updateListViewState({ filters: this.filtersForm.value, pageIndex: 0, scrollPosition: 0, selectedRecords: [] });
    switch (event.source.ngControl.name.toLowerCase()) {
      case 'versions':
        // get the selected version ids
        const versionValues: Version[] = event.value;
        // remove any existing version id filters
        this.offerpromoService.removeFilterByFieldName('versionId');
        // init the array of version filters
        const versionFilters: Filter[] = [];
        // loop through the selected version ids and add each as a filter
        versionValues.forEach((value) => {
          // init the new filter
          const versionFilter: Filter = {
            displayText: value.VersionName,
            fieldName: 'versionId',
            value: value.Id,
          };
          // add the filter to the list of filters
          versionFilters.push(versionFilter);
        });
        // add the filters to the event service
        this.offerpromoService.addFilters(versionFilters);
        break;
      case 'offertags':
        // get the selected offerTag ids
        const offerTagValues: OfferTagsCount[] = event.value;
        //remove any existing offer tags filter
        this.offerpromoService.removeFilterByFieldName('offerTagId');
        // init the array of offerTag filters
        const offerTagFilters: Filter[] = [];
        // loop through the selected offerTag ids and add each as a filter
        offerTagValues.forEach((value) => {
          // init the new filter
          if (value.TagId && value.TagName) {
            const offerTagFilter: Filter = {
              displayText: value.TagName,
              fieldName: 'offerTagId',
              value: value.TagId,
            };
            // add the filter to the list of filters
            offerTagFilters.push(offerTagFilter);
          }
        });
        // add the filters to the event service
        this.offerpromoService.addFilters(offerTagFilters);
        break;
      case 'offerpromos':
        // get the selected offerTag ids
        const eventtypeValues: PromoDomain<any>[] = event.value;
        //remove any existing offer tags filter
        this.offerpromoService.removeFilterByFieldName('eventtype');
        // init the array of offerTag filters
        const eventtypeFilters: Filter[] = [];
        // loop through the selected offerTag ids and add each as a filter
        eventtypeValues.forEach((value: any) => {
          // init the new filter
          const eventtypeFilter: Filter = {
            displayText: value.name,
            fieldName: 'eventtype',
            value: value.value,
          };
          // add the filter to the list of filters
          eventtypeFilters.push(eventtypeFilter);
        });
        // add the filters to the event service
        this.offerpromoService.addFilters(eventtypeFilters);
        break;
      case 'offerstatus':
        this.filterOfferStatus(event.value);
        break;
      case 'page':
        this.filterPages(event.value);
        break;
      case 'categories':
        const categoryValues: TProductCategory[] = event.value;
        this.offerpromoService.removeFilterByFieldName('categoryId');
        const categoryFilters: Filter[] = [];
        categoryValues.forEach((value) => {
          if (value.Id && value.ProductCategoryName) {
            const categoryFilter: Filter = {
              displayText: value.ProductCategoryName,
              fieldName: 'categoryId',
              value: value.Id,
            };
            categoryFilters.push(categoryFilter);
          }
        });
        // add the filters to the event service
        this.offerpromoService.addFilters(categoryFilters);
        break;
      case 'commentstatuses':
        const commentStatuses: string[] = event.value;
        this.offerpromoService.removeFilterByFieldName('commentStatuses');
        const commentStatusFilters: Filter[] = [];
        commentStatuses.forEach((value) => {
          if (value) {
            const commentStatusFilter: Filter = {
              displayText: value,
              fieldName: 'commentStatuses',
              value: value,
            };
            commentStatusFilters.push(commentStatusFilter);
          }
        });
        // add the filters to the event service
        this.offerpromoService.addFilters(commentStatusFilters);
        break;
      case 'workflowstages':
        const workflowStages: any = event.value;
        this.offerpromoService.removeFilterByFieldName('workflowStages');
        const workflowStageFilters: Filter[] = [];
        workflowStages.forEach((value: any) => {
          if (value) {
            const workflowStageFilter: Filter = {
              displayText: value.name,
              fieldName: 'workflowStages',
              value: value.id,
            };
            workflowStageFilters.push(workflowStageFilter);
          }
        });
        // add the filters to the event service
        this.offerpromoService.addFilters(workflowStageFilters);
        break;
    }
  }

  filterOfferStatus(event: any) {
    // get the selected status type
    const offerstatusValues: PromoDomain<any>[] = event;
    //remove any existing offer tags filter
    this.offerpromoService.removeFilterByFieldName('status');
    // init the array of offerTag filters
    const statustypeFilters: Filter[] = [];
    // loop through the selected offerTag ids and add each as a filter
    offerstatusValues.forEach((value: any) => {
      // init the new filter
      const statustypeFilter: Filter = {
        displayText: value.name,
        fieldName: 'status',
        value: value.value,
      };
      // add the filter to the list of filters
      statustypeFilters.push(statustypeFilter);
    });
    // add the filters to the event service
    this.offerpromoService.addFilters(statustypeFilters);
    this.selectedVal = this.filtersForm.controls.offerStatus.value.map(
      (x: any) => x.name
    );
  }

  filterPages(event: string[]) {
    this.offerpromoService.removeFilterByFieldName('pageids');
    if (event.length > 0) {
      const pageIdsFilters: Filter[] = [];
      event.forEach((id) => {
        let printPage = this.printPagesList.find(p => p.Id === id);
        pageIdsFilters.push({
          displayText: printPage ? printPage.PageFrom : '',
          fieldName: 'pageids',
          value: id
        });
      });
      // add the filters to the event service
      this.offerpromoService.addFilters(pageIdsFilters);
    }
  }

  noTags(event: any) {
    this.offerpromoService.removeFilterByFieldName('noTags');
    // init the new filter
    const offerTagFilter: Filter = {
      displayText: 'No Tags',
      fieldName: 'noTags',
      value: event.checked,
    };
    // add the filters to the event service
    if (event.checked) {
      this.offerpromoService.addFilters([offerTagFilter]);
    }

    this.listViewState.updateListViewState({ filters: this.filtersForm.value, pageIndex: 0, scrollPosition: 0, selectedRecords: [] });
    this.clearSelection();
  }

  withComments(event: any) {
    this.offerpromoService.removeFilterByFieldName('hasComments');
    // init the new filter
    const offerTagFilter: Filter = {
      displayText: 'Has Comments',
      fieldName: 'hasComments',
      value: event.checked,
    };
    // add the filters to the event service
    if (event.checked) {
      this.offerpromoService.addFilters([offerTagFilter]);
    }

    this.listViewState.updateListViewState({ filters: this.filtersForm.value, pageIndex: 0, scrollPosition: 0, selectedRecords: [] });
    this.clearSelection();
  }

  withVariants(event: any) {
    this.offerpromoService.removeFilterByFieldName('hasVariants');
    // init the new filter
    const hasVariantsFilter: Filter = {
      displayText: 'Has Variants',
      fieldName: 'hasVariants',
      value: event.checked,
    };
    // add the filters to the event service
    if (event.checked) {
      this.offerpromoService.addFilters([hasVariantsFilter]);
    }

    this.listViewState.updateListViewState({ filters: this.filtersForm.value, pageIndex: 0, scrollPosition: 0, selectedRecords: [] });
    this.clearSelection();
  }

  noVersions(event: any) {
    this.offerpromoService.removeFilterByFieldName('noVersions');

    const noVersionsFilter: Filter = {
      displayText: 'No Versions',
      fieldName: 'noVersions',
      value: event.checked,
    };
    // add the filters to the event service
    if (event.checked) {
      this.offerpromoService.addFilters([noVersionsFilter]);
    }

    this.listViewState.updateListViewState({ filters: this.filtersForm.value, pageIndex: 0, scrollPosition: 0, selectedRecords: [] });
    this.clearSelection();
  }

  withTranslation(event: any) {
    this.offerpromoService.removeFilterByFieldName('hasTranslation');
    const hasTranslationFilter: Filter = {
      displayText: 'Has Translation',
      fieldName: 'hasTranslation',
      value: event.checked,
    };
    if (event.checked) {
      this.offerpromoService.addFilters([hasTranslationFilter]);
    }

    this.listViewState.updateListViewState({ filters: this.filtersForm.value, pageIndex: 0, scrollPosition: 0, selectedRecords: [] });
    this.clearSelection();
  }

  resetFilters(toggleSideNav = true) {
    if (toggleSideNav == true) {
      this.sidenav.toggle();
    }
    this.clearFilters();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    if (!this.sidenav?.opened) return;

    const target = event.target as HTMLElement | null;
    if (!target) return;

    if (this.filterSidenavElement?.nativeElement.contains(target)) return;
    if (this.filterToggleButton?.nativeElement.contains(target)) return;
    if (target.closest('.cdk-overlay-container')) return;

    this.sidenav.close();
  }

  compareWithIdFn = (o1: any, o2: any) => {
    return o1 && o2 ? o1.Id === o2.Id : o1 === o2;
  };

  compareWithTagIdFn = (o1: any, o2: any) => {
    return o1 && o2 ? o1.TagId === o2.TagId : o1 === o2;
  };

  compareWithValFn = (o1: any, o2: any) => {
    return o1 && o2 ? o1.value === o2.value : o1 === o2;
  };

  deleteSelectedRecords() {
    let selectedRecordsDetails: any = this.listViewState.listViewStateValue.selectedRecords;

    if (selectedRecordsDetails && selectedRecordsDetails.length > 0) {
      const confirmDialog = this.dialog.open(ConfirmDialogComponent, {
        data: {
          title: 'Confirm Delete',
          message: `Do you want to remove selected Offer/Promo?`,
        },
        disableClose: true,
      });
      confirmDialog.afterClosed().subscribe((confirmResult) => {
        if (confirmResult) {
          let bulkDelete: any = [];
          for (let index = 0; index <= selectedRecordsDetails.length - 1; index++) {
            const activeVersion = selectedRecordsDetails[index].Variants.find((x: any) => x.activeItem == true);
            if (!activeVersion || (activeVersion && activeVersion.Id === selectedRecordsDetails[index].DomainId)) {
              if (selectedRecordsDetails[index].EventOfferType == "OFFERGROUP") {
                const x = selectedRecordsDetails[index].Detail;
                bulkDelete.push({
                  Id: x.Id,
                  ClientKey: x.ClientKey,
                  UserId: x.UserId,
                  Name: x.Name,
                  Headline: x.Headline,
                  BodyCopy: x.BodyCopy,
                  Disclaimer: x.Disclaimer,
                  ClientComment: x.ClientComment,
                  StartDate: dayjs(x.StartDate).format('MM/DD/YYYY'),
                  EndDate: dayjs(x.EndDate).format('MM/DD/YYYY'),
                  Rank: x.Rank,
                  TargetURL: x.TargetURL,
                  Status: x.Status,
                  Version: selectedRecordsDetails[index].VersionNames,
                  Label: selectedRecordsDetails[index].EventOfferType,
                });
              } else {
                const recordstoupdate = selectedRecordsDetails[index].Variants.map((x: any) => {
                  return {
                    Id: x.Id,
                    ClientKey: x.ClientKey,
                    UserId: x.UserId,
                    Name: x.Name,
                    Headline: x.Headline,
                    BodyCopy: x.BodyCopy,
                    Disclaimer: x.Disclaimer,
                    ClientComment: x.ClientComment,
                    StartDate: dayjs(x.StartDate).format('MM/DD/YYYY'),
                    EndDate: dayjs(x.EndDate).format('MM/DD/YYYY'),
                    Rank: x.Rank,
                    TargetURL: x.TargetURL,
                    Status: x.Status,
                    Version: x.Version,
                    Label: selectedRecordsDetails[index].EventOfferType,
                  };
                });
                bulkDelete = [...bulkDelete, ...recordstoupdate];
              }
            } else if (activeVersion && activeVersion.Id != selectedRecordsDetails[index].DomainId) {
              const x = activeVersion;
              bulkDelete.push({
                Id: x.Id,
                ClientKey: x.ClientKey,
                UserId: x.UserId,
                Name: x.Name,
                Headline: x.Headline,
                BodyCopy: x.BodyCopy,
                Disclaimer: x.Disclaimer,
                ClientComment: x.ClientComment,
                StartDate: dayjs(x.StartDate).format('MM/DD/YYYY'),
                EndDate: dayjs(x.EndDate).format('MM/DD/YYYY'),
                Rank: x.Rank,
                TargetURL: x.TargetURL,
                Status: x.Status,
                Version: x.Version,
                Label: selectedRecordsDetails[index].EventOfferType,
              });
            }
          }
          this.offerpromoService.deleteOfferPromos(bulkDelete).subscribe({
            next: () => {
              this.matSnackBar.open(`Offer/Promo(s) deleted`, 'OK', DEFAULT_SNACKBAR_CONFIG);
              this.listViewState.updateListViewState({ filters: this.filtersForm.value, pageIndex: 0, scrollPosition: 0, selectedRecords: [] });
              this.offerpromoService.page({ pageIndex: 0, pageSize: this.listViewState.listViewStateValue.pageSize });
              this.offerpromoService.reload();
              this.clearSelection();
              this.getStatusCount();
            },
            error: (error) => {
              log.error('Error in deleting Offer/Promo', error);

              if (error.error.value) {
                throw new Error(error.error.value);
              } else {
                throw new Error(error.message);
              }
            },
          });
        }
      });
    }
  }

  downloadOffersJSON() {
    this.offerpromoService.downloadOffersJSON(
      this.route.snapshot.params['eventId']
    );
  }

  downloadOfferGroupJSON() {
    this.offerpromoService.downloadOfferGroupJSON(
      this.route.snapshot.params['eventId']
    );
  }

  updateStatus(status: Status) {
    let selectedRecordsDetails: any = JSON.parse(JSON.stringify(this.listViewState.listViewStateValue.selectedRecords));

    if (selectedRecordsDetails && selectedRecordsDetails.length > 0) {
      let statustoUpdate: any = [];
      for (let index = 0; index <= selectedRecordsDetails.length - 1; index++) {
        const activeVersion = selectedRecordsDetails[index].Variants.find(
          (x: any) => x.activeItem == true
        );
        if (
          !activeVersion ||
          (activeVersion &&
            activeVersion.Id === selectedRecordsDetails[index].DomainId)
        ) {
          if (selectedRecordsDetails[index].EventOfferType == "OFFERGROUP") {
            const x = selectedRecordsDetails[index];
            statustoUpdate.push({
              Id: x.Detail.Id,
              Status: status,
              Label: selectedRecordsDetails[index].EventOfferType,
            });
          } else {
            const recordstoupdate = selectedRecordsDetails[
              index
            ].Variants.map((x: any) => {
              return {
                Id: x.Id,
                Status: status,
                Label: selectedRecordsDetails[index].EventOfferType,
              };
            });
            statustoUpdate = [...statustoUpdate, ...recordstoupdate];
          }
        } else if (
          activeVersion &&
          activeVersion.Id != selectedRecordsDetails[index].DomainId
        ) {
          const x = activeVersion;
          statustoUpdate.push({
            Id: x.Id,
            Status: status,
            Label: selectedRecordsDetails[index].EventOfferType,
          });
        }
      }

      this.offerpromoService.updateStatus(statustoUpdate).subscribe({
        next: () => {
          this.offerpromoService.reload();
          this.clearSelection();
          this.getStatusCount();
        },
      });
    }
  }

  filterVariants(record: any): any[] {
    // Check if record.Detail.Id is equal to any Variant.BaseId
    if (record.Detail.Id === record.Variants[0]?.BaseId) {
      return record.Variants; // Return all Variants
    }

    return record.Variants.filter((variant: any) => variant.Id === record.Detail.Id);
  }

  downloadPromosJSON() {
    this.offerpromoService.downloadPromosJSON(
      this.route.snapshot.params['eventId']
    );
  }

  downloadEventJSON() {
    this.offerpromoService.downloadEventJSON(
      this.route.snapshot.params['eventId']
    );
  }

  private downloadOffersCSV(ids?: string[]) {
    this.offerpromoService.downloadCSV(this.route.snapshot.params['eventId'], ids).subscribe(res => {
      const contentDisposition = res.headers.get('content-disposition');
      let fileName = 'offers.csv';

      if (contentDisposition) {
        const utf8FileNameMatch = contentDisposition.match(/filename\*\s*=\s*UTF-8''([^;]+)/);
        if (utf8FileNameMatch && utf8FileNameMatch[1]) {
          fileName = decodeURIComponent(utf8FileNameMatch[1]);
        } else {
          const fileNameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
          if (fileNameMatch && fileNameMatch[1]) {
            fileName = fileNameMatch[1];
          }
        }
      }

      const blob = new Blob([res.body!], { type: res.body!.type });
      const url = window.URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = fileName;
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      window.URL.revokeObjectURL(url);
    });
  }

  downloadAllCSV() {
    this.downloadOffersCSV();
  }

  downloadSelectedCSV() {
    const selectedIds = (this.listViewState.listViewStateValue.selectedRecords ?? [])
      .map((record: any) => String(record.Detail?.Id ?? record.DomainId))
      .filter((id: string) => !!id);

    if (!selectedIds.length) {
      this.matSnackBar.open('Please select at least one offer to export', 'OK', DEFAULT_SNACKBAR_CONFIG);
      return;
    }

    this.downloadOffersCSV(selectedIds);
  }

  downloadCommentsCSV() {
    this.offerpromoService.downloadCommentsCSV(this.route.snapshot.params['eventId']);
  }

  searchOfferTags(event: any) {
    this.offerTagService.search(event.target.value);
  }

  tagSelected(event: any) {
    this.offerpromoService.removeFilterByFieldName('offerTagId');
    this.selectedTag = event.option.value.OfferTagName;
    this.filtersForm['controls']['offerTags'].setValue(event.option.value.Id);
    const offerTagFilters: Filter[] = [
      {
        displayText: event.option.value.OfferTagName,
        fieldName: 'offerTagId',
        value: event.option.value.Id,
      },
    ];
    // add the filters to the event service
    this.offerpromoService.addFilters(offerTagFilters);
  }

  openVersionsDialog(mode: 'add' | 'remove'): void {
    let selectedRecordsDetails: any = JSON.parse(JSON.stringify(this.listViewState.listViewStateValue.selectedRecords));

    if (mode === 'remove') {
      const keys: string[] = (selectedRecordsDetails ?? []).flatMap(
        (r: any) =>
          r.Variants?.length ? r.Variants.map((v: any) => String(v.Id)) : [String(r.Detail?.Id ?? r.DomainId)]
      );
      this.versionService.setVersionDialogKeys(keys);
    }

    const dialogRef = this.dialog.open(VersionListComponent, {
      width: '80%',
      height: '90%',
      data: {
        eventId: this.route.snapshot.params['eventId'],
        selectedRecordsDetails,
        mode
      },
    });
    this.versionService.search('');
    this.route.params.subscribe((params) => {
      this.versionService.eventId = params['eventId'];
    });
    dialogRef.afterClosed().subscribe(() => {
      this.versionService.clearVersionDialogKeys();
    });
  }

  openofferTagsDialog() {
    let selectedRecordsDetails = JSON.parse(JSON.stringify(this.listViewState.listViewStateValue.selectedRecords));

    const dialogRef = this.dialog.open(EditOfferTagsComponent, {
      width: '80%',
      height: '90%',
      autoFocus: false,
      data: {
        eventId: this.route.snapshot.params['eventId'],
        selectedRecordsDetails,
      },
    });
    dialogRef.afterClosed().subscribe((res: any) => {
      if (res === 'success') {
        this.subscription$.add(this.offerTagService.getByOfferTagsWithCount(
          this.route.snapshot.params['eventId'] || this.offerpromoService.eventId, []
        ).pipe(take(1)).subscribe((results) => {
          this.offerTagsListwithCount = results.filter(tag => tag.TagId && tag.TagName);
        }));

        this.clearSelection();
      }
    });
  }

  openSelectedRecordsDialog() {
    this.dialog.open(SelectedRecordsDialogComponent, {
      width: '95vw',
      maxWidth: '900px',
      maxHeight: '85vh',
      autoFocus: false,
      viewContainerRef: this.viewContainerRef,
      data: { eventId: this.route.snapshot.params['eventId'] },
    });
  }

  openEditDatesDialog() {
    const formatDate = (date: any) => date ? dayjs(date).format('MM/DD/YYYY') : '';

    let defaultStartDate = formatDate(this.eventDetails?.StartDate);
    let defaultEndDate = formatDate(this.eventDetails?.EndDate);

    const selectedRecordsDetails = JSON.parse(JSON.stringify(this.listViewState.listViewStateValue.selectedRecords));
    if (!selectedRecordsDetails?.length) return;

    if (selectedRecordsDetails.length === 1) {
      const detail = selectedRecordsDetails[0]?.Detail;
      defaultStartDate = formatDate(detail?.StartDate);
      defaultEndDate = formatDate(detail?.EndDate);
    }

    const dialogRef = this.dialog.open(EditDatesDialogComponent, {
      data: { defaultStartDate, defaultEndDate },
    });

    dialogRef.afterClosed().subscribe((res: any) => {
      if (!res) return;

      const datesToUpdateList = selectedRecordsDetails.flatMap((record: any) => {
        const formattedStart = formatDate(res.StartDate);
        const formattedEnd = formatDate(res.EndDate);

        if (record.EventOfferType === 'OFFERGROUP') {
          return [{
            Id: record.Detail?.Id,
            StartDate: formattedStart,
            EndDate: formattedEnd,
            Label: record.EventOfferType,
          }];
        }

        const variants = this.filterVariants(record);
        return variants.map((variant: any) => ({
          Id: variant.Id,
          StartDate: formattedStart,
          EndDate: formattedEnd,
          Label: record.EventOfferType,
        }));
      });

      if (!datesToUpdateList.length) return;

      this.offerpromoService.updateDates(datesToUpdateList).subscribe({
        next: () => {
          this.matSnackBar.open('Start and End dates updated', 'OK', DEFAULT_SNACKBAR_CONFIG);
          this.clearSelection();
        },
        error: (err: any) => {
          this.matSnackBar.open('Failed to update dates', 'OK', DEFAULT_SNACKBAR_CONFIG);
          console.error('Failed to update dates', err);
        }
      });
    });
  }

  openEditPageDialog() {
    let selectedRecordsDetails = JSON.parse(JSON.stringify(this.listViewState.listViewStateValue.selectedRecords));

    const dialogRef = this.dialog.open(EditPageComponent, {
      width: '40%',
      height: '90%',
      autoFocus: false,
      data: {
        eventId: this.route.snapshot.params['eventId'],
        selectedRecordsDetails,
      },
    });
    dialogRef.afterClosed().subscribe(res => {
      if (res) {
        this.offerpromoService.reload();
        this.clearSelection();
      }
    })
  }

  markStageAsComplete() {
    console.log(`this.activeStage: `, this.activeStage);
    const workflowId = this.awDetails.Id;
    const taskId = this.activeStage?.StageId;
    const stageName = this.activeStage?.StageName;
    /** Logs for testing */
    console.log('Clicked on MarkStageAsComplete. Details: ', {
      workflowId,
      taskId,
      stageName,
      eventId: this.route.snapshot.params['eventId'],
      eventName: this.eventDetails?.EventName,
    });

    const message = `Are you sure you wish to mark this stage as complete?`;

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: 'Mark Complete',
        message: message,
        confirmText: 'Confirm',
        cancelText: 'Cancel',
      },
    });

    this.subscription$.add(
      dialogRef.afterClosed().subscribe((result) => {
        if (result == true) {
          this.onConfirmMarkStageAsComplete();
        }
      })
    );
  }

  onConfirmMarkStageAsComplete() {
    const workflowId = this.awDetails.Id;
    const taskId = this.activeStage?.StageId;
    const stageName = this.activeStage?.StageName;
    const stageCompleteMsg = `Stage <b>${stageName}</b> is completed for event <b>${this.eventDetails?.EventName}</b>`;
    this.setCurrentStageUsers(taskId);
    this.ewService
      .updateStageStatus(
        workflowId,
        taskId!,
        this.orgService.silentAuthToken,
        'completed',
        stageCompleteMsg
      )
      .subscribe((resp: any) => {
        this.stageCompleted = true;
        this.notificationService.showSuccess(
          `${this.awDetails?.TemplateName} - ${stageName} complete`
        );
        if (this.stageToMarkActive != null) {
          this.setCurrentStageUsers(this.stageToMarkActive?.StageId);
          const urlWithDomainAndEventId = window.location.href.substring(
            0,
            window.location.href.lastIndexOf('/')
          );
          const emailTemplateString = this.ewService.getEmailBody(
            this.eventDetails?.EventName,
            this.awDetails?.TemplateName,
            this.stageToMarkActive?.StageName,
            `${urlWithDomainAndEventId}/offers`
          );
          this.ewService
            .updateStageStatus(
              workflowId,
              this.stageToMarkActive?.StageId,
              this.orgService.silentAuthToken,
              'active',
              `Stage <b>${this.stageToMarkActive?.StageName}</b> is marked Active for event ${this.eventDetails?.EventName}</b>`,
              emailTemplateString
            )
            .subscribe((markActiveResponse: any) => {
              setTimeout(() => {
                let currentUrl = this.router.url;
                this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
                  this.router.navigate([currentUrl]);
                });
              }, 3000);
            })
        } else {
          setTimeout(() => {
            let currentUrl = this.router.url;
            this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
              this.router.navigate([currentUrl]);
            });
          }, 3000);
        }
      })
  }

  setCurrentStageUsers(taskId: string) {
    if (this.applicableWorkflowPermissions && this.applicableWorkflowPermissions.Stages && this.applicableWorkflowPermissions.Stages.length > 0) {
      const currentStage = this.applicableWorkflowPermissions.Stages.find((x: any) => x.StageId === taskId);
      if (currentStage && currentStage.Users && currentStage.Users.length > 0) {
        const emailsList = currentStage.Users.map((x: any) => {
          x.email = x.Email ? x.Email : x.email;
          x.email_alerts = x.EmailAlert ?? x.email_alerts;
          return x;
        });
        this.ewService.setUsersOfActiveStage(
          this.ewService.filterNotifiableUsers(emailsList)
        );
      }
    }
  }

  triggerEmailForActiveUsersInNextStage() {
    const stages = this.applicableWorkflowPermissions?.Stages;
    const activeStageIndex = stages.findIndex((s: any) => s.Status == 'active');

    if (
      activeStageIndex != undefined &&
      activeStageIndex > -1 &&
      stages.length > activeStageIndex + 1
    ) {
      const stageToMarkActive =
        this.applicableWorkflowPermissions?.Stages[activeStageIndex + 1];
      const urlWithDomainAndEventId = window.location.href.substring(
        0,
        window.location.href.lastIndexOf('/')
      );

      const eventId = window.location.hash.split('/')[3];

      // const emailTemplateString = `<html><body>Stage <b>Stage ${stageToMarkActive.StageName
      //   }</b> of Workflow <b>${this.awDetails?.TemplateName // -------------------> GetWorkflowName!
      //   }</b>,  is now Active.<br /> Access the Event here:<br />  ${urlWithDomainAndEventId}/offers</body></html>`;

      const emailTemplateString = this.ewService.getEmailBody(
        this.eventDetails?.EventName,
        this.awDetails?.TemplateName,
        stageToMarkActive.StageName,
        `${urlWithDomainAndEventId}/offers`
      );

      const inappTemplateString = this.ewService.getInappBody(
        this.eventDetails?.EventName,
        this.awDetails?.TemplateName,
        stageToMarkActive.StageName,
        `${urlWithDomainAndEventId}/offers`
      );

      const notifiableEmails = this.ewService.getNotifiableEmails(
        stageToMarkActive.Users ?? stageToMarkActive.users
      );
      if (notifiableEmails.length > 0) {
        this.ewService.triggerCustomKnockNotification(
          notifiableEmails,
          emailTemplateString,
          '$users',
          'pr1-notification',
          inappTemplateString
        );
      }
    }
  }

  ExportSelectedGalleyProof() {
    let selectedOfferPromos = this.listViewState.listViewStateValue.selectedRecords.map((record: any) => record.DomainId);

    if (selectedOfferPromos.length) {
      this.offerpromoService.ExportSelectedGalleyProof(this.eventDetails.Id, selectedOfferPromos).subscribe(res => {

        const contentDisposition = res.headers.get('content-disposition');
        let fileName = 'galleyProof.pdf';

        if (contentDisposition) {
          const utf8FileNameMatch = contentDisposition.match(/filename\*\s*=\s*UTF-8''([^;]+)/);
          if (utf8FileNameMatch && utf8FileNameMatch[1]) {
            fileName = decodeURIComponent(utf8FileNameMatch[1]);
          } else {
            const fileNameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
            if (fileNameMatch && fileNameMatch[1]) {
              fileName = fileNameMatch[1];
            }
          }
        }

        const blob = new Blob([res.body!], { type: res.body!.type });
        const url = window.URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = fileName;
        document.body.appendChild(anchor);
        anchor.click();
        document.body.removeChild(anchor);
        window.URL.revokeObjectURL(url);
      })
    }
  }

  exportGalleyProof() {
    this.offerpromoService.exportAllGalleyProof(this.eventDetails.Id).subscribe(res => {

      const contentDisposition = res.headers.get('content-disposition');
      let fileName = 'galleyProof.pdf';

      if (contentDisposition) {
        const utf8FileNameMatch = contentDisposition.match(/filename\*\s*=\s*UTF-8''([^;]+)/);
        if (utf8FileNameMatch && utf8FileNameMatch[1]) {
          fileName = decodeURIComponent(utf8FileNameMatch[1]);
        } else {
          const fileNameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
          if (fileNameMatch && fileNameMatch[1]) {
            fileName = fileNameMatch[1];
          }
        }
      }

      const blob = new Blob([res.body!], { type: res.body!.type });
      const url = window.URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = fileName;
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      window.URL.revokeObjectURL(url);
    })
  }
}
